// src/presentation/contexts/FavoriteContext.tsx - SOLUCIÓN SIMPLE SIN LOOPS
import React, {createContext, useState, useCallback, useEffect, useRef} from "react";
import type {ReactNode} from "react";
import ApiClient from "../../infrastructure/api/apiClient";
import {API_ENDPOINTS} from "../../constants/apiEndpoints";
import type {
	Favorite,
	FavoriteListResponse,
} from "../../core/domain/entities/Favorite";
import {useAuth} from "../hooks/useAuth";
import CacheService from "../../infrastructure/services/CacheService";

interface FavoriteContextProps {
	favorites: Favorite[];
	loading: boolean;
	error: string | null;
	favoriteCount: number;
	toggleFavorite: (productId: number) => Promise<boolean>;
	checkIsFavorite: (productId: number) => boolean;
	fetchFavorites: () => Promise<void>;
	isProductFavorite: (productId: number) => boolean;
}

export const FavoriteContext = createContext<FavoriteContextProps>({
	favorites: [],
	loading: false,
	error: null,
	favoriteCount: 0,
	toggleFavorite: async () => false,
	checkIsFavorite: () => false,
	fetchFavorites: async () => {},
	isProductFavorite: () => false,
});

interface FavoriteProviderProps {
	children: ReactNode;
}

export const FavoriteProvider: React.FC<FavoriteProviderProps> = ({
	children,
}) => {
	const [favorites, setFavorites] = useState<Favorite[]>([]);
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [favoriteCount, setFavoriteCount] = useState<number>(0);
	const {isAuthenticated} = useAuth();
	const hasInitializedRef = useRef(false);
	const isFetchingRef = useRef(false);

	// ✅ FUNCIÓN SIMPLE PARA FETCH SIN LOOPS
	const fetchFavorites = useCallback(async () => {
		if (!isAuthenticated) {
			setFavorites([]);
			setFavoriteCount(0);
			return;
		}

		if (isFetchingRef.current) return;
		isFetchingRef.current = true;

		// Verificar cache primero
		const cachedFavorites = CacheService.getItem("user_favorites");
		if (cachedFavorites) {
			console.log("📦 Using cached favorites");
			setFavorites(cachedFavorites);
			setFavoriteCount(cachedFavorites.length);
			isFetchingRef.current = false;
			return;
		}

		setLoading(true);
		setError(null);

		try {
			console.log("🌐 Fetching favorites from API");
			const response = await ApiClient.get<FavoriteListResponse>(
				API_ENDPOINTS.FAVORITES.LIST
			);

			if (response && response.data) {
				const favoritesData = Array.isArray(response.data) ? response.data : [];
				const normalizedFavorites = favoritesData.map((item: any) => {
					const favoriteData = item?.favorite ?? item ?? {};
					const productData = item?.product ?? favoriteData?.product ?? null;
					const productId =
						favoriteData?.productId ??
						favoriteData?.product_id ??
						productData?.id ??
						item?.productId ??
						item?.product_id;

					return {
						...favoriteData,
						productId,
						userId: favoriteData?.userId ?? favoriteData?.user_id ?? 0,
						notifyPriceChange:
							favoriteData?.notifyPriceChange ??
							favoriteData?.notify_price_change ??
							true,
						notifyPromotion:
							favoriteData?.notifyPromotion ??
							favoriteData?.notify_promotion ??
							true,
						notifyLowStock:
							favoriteData?.notifyLowStock ??
							favoriteData?.notify_low_stock ??
							true,
						product: productData,
					} as Favorite;
				});

				console.log(`✅ Favoritos cargados: ${normalizedFavorites.length}`);

				setFavorites(normalizedFavorites);
				setFavoriteCount(normalizedFavorites.length);

				// Guardar en cache por 10 minutos
				CacheService.setItem("user_favorites", normalizedFavorites, 10 * 60 * 1000);
			} else {
				setFavorites([]);
				setFavoriteCount(0);
			}
		} catch (err) {
			console.error("Error fetching favorites:", err);
			setError(err instanceof Error ? err.message : "Error al cargar favoritos");
			setFavorites([]);
			setFavoriteCount(0);
		} finally {
			setLoading(false);
			isFetchingRef.current = false;
		}
	}, [isAuthenticated]);

	// Initialize context but don't auto-fetch favorites (use header-counters for count)
	useEffect(() => {
		if (!isAuthenticated) {
			setFavorites([]);
			setFavoriteCount(0);
			hasInitializedRef.current = false;
			return;
		}

		// Hydrate from cache or fetch once lazily for correct card state
		if (!hasInitializedRef.current) {
			hasInitializedRef.current = true;

			const cachedFavorites = CacheService.getItem("user_favorites");
			if (cachedFavorites) {
				setFavorites(cachedFavorites);
				setFavoriteCount(cachedFavorites.length);
				return;
			}

			// Fetch in background to avoid blocking render
			setTimeout(() => {
				fetchFavorites();
			}, 0);
		}
	}, [isAuthenticated, fetchFavorites]);

	// ✅ CHECK SINCRÓNICO
	const checkIsFavorite = useCallback(
		(productId: number): boolean => {
			if (!isAuthenticated) return false;
			return favorites.some(
				(fav) => fav.productId === productId || fav.product?.id === productId
			);
		},
		[favorites, isAuthenticated]
	);

	// Alias para mayor claridad
	const isProductFavorite = checkIsFavorite;

	// ✅ TOGGLE SIMPLE
	const toggleFavorite = useCallback(
		async (productId: number): Promise<boolean> => {
			if (!isAuthenticated) {
				console.log("🚫 No autenticado, no se puede toggle favorite");
				return false;
			}

			const wasFavorite = checkIsFavorite(productId);

			try {
				console.log(
					`🔄 Toggling favorite for product ${productId} (was: ${wasFavorite})`
				);

				const response = await ApiClient.post<{
					data?: {is_favorite?: boolean};
					is_favorite?: boolean;
					isFavorite?: boolean;
				}>(
					API_ENDPOINTS.FAVORITES.TOGGLE,
					{productId}
				);

				// Invalidar cache y refetch
				CacheService.removeItem("user_favorites");
				CacheService.removeItem("header_counters");
				await fetchFavorites();

				const newState =
					response?.data?.is_favorite ??
					response?.is_favorite ??
					response?.isFavorite ??
					!wasFavorite;

				console.log(
					`✅ Favorite toggled: product ${productId} is now ${newState ? "favorited" : "unfavorited"}`
				);

				return newState;
			} catch (err) {
				console.error("Error toggling favorite:", err);
				return wasFavorite;
			}
		},
		[isAuthenticated, checkIsFavorite, fetchFavorites]
	);

	return (
		<FavoriteContext.Provider
			value={{
				favorites,
				loading,
				error,
				favoriteCount,
				toggleFavorite,
				checkIsFavorite,
				fetchFavorites,
				isProductFavorite,
			}}
		>
			{children}
		</FavoriteContext.Provider>
	);
};
